// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Checkbox,
  FormItem,
  Note,
  Form,
  Input,
  Modal,
  Select,
  TextArea,
  UseShowHide,
  useForm,
} from '@teamnine/pcj-ui-v2'

import { initiateTenantExport } from '@/services/provisioning'
import { Tenant as Practice } from '@/services/practices'
import { useGetObjectDefinitions } from '@/app/CustomerManagement/hooks/useGetObjectDefinitions'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'

type ExportPracticeModalProps = {
  modal: UseShowHide
  practice?: Practice
}

type ExportPracticeModalFormValues = {
  name: string
  description?: string
  includeData?: boolean
  definitions?: string[]
}

export default function ExportPracticeModal({
  modal,
  practice,
}: ExportPracticeModalProps) {
  const navigate = useNavigate()
  const [form] = useForm()
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()

  const [isLoading, setIsLoading] = useState(false)
  const [includeData, setIncludeData] = useState(false)

  const FORM_NAME = 'practice-export'

  const { data: objectDefinitions, isLoading: isLoadingObjectDefinitions } =
    useGetObjectDefinitions(practice?.id ?? '')

  const objectDefinitionOptions = useMemo(
    () =>
      objectDefinitions?.map((objectDefinition: string) => ({
        label: objectDefinition,
        value: objectDefinition,
      })) ?? [],
    [objectDefinitions]
  )

  const onFormFinish = useCallback(
    async (values: ExportPracticeModalFormValues) => {
      if (!practice) return
      try {
        setIsLoading(true)

        const { name, description, definitions } = values

        const response = await initiateTenantExport({
          tenantId: practice.id,
          templateName: name,
          templateDescription: description ?? '',
          includeObjectData: includeData,
          excludedObjectDefinitions: definitions ?? [],
        })

        displayGlobalMessage({
          heading: 'Practice exported successfully.',
          type: 'success',
        })

        modal.hide()
        navigate(`/templates/${response.templateId}`)
      } catch (err) {
        basicErrorHandler(err)
      } finally {
        setIsLoading(false)
      }
    },
    [
      practice,
      includeData,
      basicErrorHandler,
      displayGlobalMessage,
      modal,
      navigate,
    ]
  )

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Display aria-label="Export Practice">
        <Modal.Header title="Export Practice" />
        <Modal.Content>
          <Form name={FORM_NAME} form={form} onFinish={onFormFinish}>
            <FormItem
              required
              label="Template Name"
              name="name"
              initialValue={practice?.name ?? ''}
            >
              <Input placeholder="Enter template name" />
            </FormItem>
            <FormItem label="Template Description" name="description">
              <TextArea />
            </FormItem>
            <FormItem name="includeData">
              <Checkbox
                checked={includeData}
                onChange={() => {
                  setIncludeData((prev) => {
                    const next = !prev
                    if (!next) {
                      form.setFieldValue('definitions', [])
                    }
                    return next
                  })
                }}
              >
                Include Data
              </Checkbox>
            </FormItem>
            <FormItem label="Excluded Object Definitions" name="definitions">
              <Select
                placeholder="Choose object definitions"
                mode="multiple"
                options={objectDefinitionOptions}
                loading={isLoadingObjectDefinitions}
                disabled={!includeData}
              />
            </FormItem>
            <Note>
              Exporting the practice will capture existing configurations and/or
              data to create a new template.
            </Note>
          </Form>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Export',
              form: FORM_NAME,
              type: 'submit',
              loading: isLoading,
              disabled: isLoading,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
